<template>
  <div class="wrapper inline-grid grid-cols-9 col-gap width-full">
    <header class="page-title col-span-6 col-start-2">
      <h1 class="header-one">
        Border width
      </h1>
      <p class="page-title__sub">
        Utility classes for border width.
      </p>
    </header>
    <section class="section col-span-6 col-start-2">
      <p>
        Naming of border width classes will change from project to project,
        depending on the widths required. Where<code class="margin-right-5">{width}</code>
        appears below this will represent the variables you add.
      </p>
      <div class="table">
        <table>
          <thead>
            <tr>
              <th>Class name</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code class="code__class">.border-{width}</code>
              </td>
              <td>
                <code class="code__prop">border-width: {width};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-top-{width}</code>
              </td>
              <td>
                <code class="code__prop">border-top-width: {width};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-bottom-{width}</code>
              </td>
              <td>
                <code class="code__prop">border-bottom-width: {width};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-left-{width}</code>
              </td>
              <td>
                <code class="code__prop">border-left-width: {width};</code>
              </td>
            </tr>
            <tr>
              <td>
                <code class="code__class">.border-right-{width}</code>
              </td>
              <td>
                <code class="code__prop">border-right-width: {width};</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          All sides
        </h2>
      </header>
      <p>
        To set the border width to an element, suffix the width
        <code>.{-width}</code>, for example <code>.border-1</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="border-0 border-solid border-river-bed..."&gt;&lt;/div&gt;
&lt;div class="border-1 border-solid border-river-bed..."&gt;&lt;/div&gt;
&lt;div class="border-2 border-solid border-river-bed..."&gt;&lt;/div&gt;
&lt;div class="border-4 border-solid border-river-bed..."&gt;&lt;/div&gt;
&lt;div class="border-6 border-solid border-river-bed..."&gt;&lt;/div&gt;
&lt;div class="border-8 border-solid border-river-bed..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-6 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-0</code>
              <span class="preview__button border-solid border-0 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-1</code>
              <span class="preview__button border-solid border-1 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-2</code>
              <span class="preview__button border-solid border-2 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-4</code>
              <span class="preview__button border-solid border-4 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-6</code>
              <span class="preview__button border-solid border-6 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-8</code>
              <span class="preview__button border-solid border-8 border-river-bed bg-tiara" />
            </div>
          </div>
        </template>
      </preview>
    </section>
    <section class="section col-span-6 col-start-2">
      <header class="section__title">
        <h2 class="header-two">
          Individual sides
        </h2>
      </header>
      <p>
        To set the border width of an element on a particular side, suffix the position
        <code class="margin-right-5">{-top | bottom | left | right}</code> and then the width
        <code>{-width}</code>, for example <code>.border-top-2</code>.
      </p>
      <code-block>
        <template #code>
          <pre class="language-html" rel="html">
          <code>&lt;div class="border-top-2 border-solid border-tiara..."&gt;&lt;/div&gt;
&lt;div class="border-right-2 border-solid border-tiara border-top-10..."&gt;&lt;/div&gt;
&lt;div class="border-bottom-2 border-solid border-tiara border-top-10..."&gt;&lt;/div&gt;
&lt;div class="border-left-2 border-solid border-tiara border-top-10..."&gt;&lt;/div&gt;</code>
          </pre>
        </template>
      </code-block>
      <preview>
        <template #preview>
          <div class="preview__wrapper grid grid-cols-6 col-gap row-gap
          l-col-gap l-row-gap m-col-gap m-row-gap sm-col-gap sm-row-gap
          align-items-start width-full"
          >
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-top-1</code>
              <span class="preview__button border-solid border-top-2 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-right-1</code>
              <span class="preview__button border-solid border-right-2 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border--bottom-2</code>
              <span class="preview__button border-solid border-bottom-2 border-river-bed bg-tiara" />
            </div>
            <div class="flex flex-col col-span-2">
              <code class="code inline-flex margin-bottom-5">.border-left-4</code>
              <span class="preview__button border-solid border-left-2 border-river-bed bg-tiara" />
            </div>
          </div>
        </template>
      </preview>
    </section>
  </div>
</template>

<script>
import Preview from '@/components/Preview';
import CodeBlock from '@/components/Code-Block';

export default {
  name: 'BrdWidth',
  components: {
    Preview,
    CodeBlock,
  },
};
</script>
